import {
  Button,
  GenericLoader,
  If,
  ImageProfile,
  Input,
  mage,
  Otp,
  Toggle,
  useHasRequiredFormError,
  useI18n,
  widgets,
} from '@nosinovacao/nosid-mfe-common';
import { type CreateProfileProps } from '../-model/model';
import { Link } from '@tanstack/react-router';
import { AvatarPictureWooDark } from '../../-components/avatar-picture.woo-dark';
import { WooChange } from '@nosinovacao/fe-icons';

export function CreateTvProfileWooDark({
  form,
  onSubmit,
  formValueHash,
  isKidsProfile,
  isDeletingProfile,
  isEditing,
  sa,
  profileId,
  hasPin,
  validationState,
}: Readonly<CreateProfileProps>) {
  const { t } = useI18n();
  const confirmPinErrors = form.currentErrors.confirmPin;
  const hasRequiredformError = useHasRequiredFormError(form) || !!confirmPinErrors?.length;
  return (
    <form
      data-tid="form-create-profile"
      className="flex size-full flex-col items-center gap-4 overflow-y-auto px-1"
      onSubmit={form.handleSubmit(onSubmit)}
    >
      <Link
        data-tid="link-select-avatar"
        to="/tv/$id/profiles/select-avatar"
        params={{ id: sa }}
        search={{ kids: isKidsProfile, profileId, hasPin }}
        state={validationState as never}
        hash={formValueHash}
        className="relative size-fit"
      >
        <AvatarPictureWooDark
          src={mage.getImage(form.value.avatar?.ImageUri, ImageProfile.Avatar)}
          size={96}
          name={null}
        />
        <div className="absolute -bottom-1 right-[-6px] grid size-8 place-items-center rounded-full border border-nos-neutral-700 bg-nos-neutral-0 text-nos-neutral-1000">
          <WooChange width={22} height={22} />
        </div>
      </Link>
      <div className="grid w-full gap-6">
        <Input
          data-tid="input-profile-name"
          label={'TvProfiles.CreateProfile.Inputs.Name'}
          id={form.ids.name ?? 'name'}
          skin="woo-dark"
          value={form.value.name}
          errors={form.errors.name}
          onChange={(ev) => {
            form.setValue('name', ev.target.value);
          }}
        />
        <If condition={isKidsProfile}>
          <div className="grid gap-2" role="radiogroup">
            <span data-tid="text-age-rating" className="font-light text-nos-neutral-0 font-text-p1">
              {t('TvProfiles.CreateProfile.Inputs.AgeRating')}
            </span>
            {widgets.AgeRatings.map((ar) => (
              <button
                data-tid="button-age-rating"
                type="button"
                aria-pressed={form.value.ageRating === ar.TopSegmentKey}
                onClick={() => {
                  form.setValue('ageRating', ar.TopSegmentKey);
                }}
                key={ar.TopSegmentKey}
                className="grid grid-cols-[1fr_1.25rem] gap-1 rounded border border-nos-neutral-800 bg-nos-neutral-900 p-4 text-start"
              >
                <span className="font-light text-nos-neutral-200 font-text-p2">
                  {t('TvProfiles.CreateProfile.AgeRatings.Prefix')}
                </span>
                <div className="row-span-2 grid place-items-center">
                  <div
                    data-selected={form.value.ageRating === ar.TopSegmentKey}
                    className="grid size-5 place-items-center rounded-full border border-nos-neutral-0 after:size-3 after:rounded-full data-[selected=true]:after:bg-woo-green"
                  />
                </div>
                <span className="font-bold text-nos-neutral-0 font-ui-8">
                  {t(`TvProfiles.CreateProfile.AgeRatings.Segments.${ar.TopSegmentKey}`)}
                </span>
              </button>
            ))}
          </div>
        </If>
        <If condition={!isKidsProfile}>
          <div className="grid grid-rows-1 overflow-hidden border border-nos-neutral-500 bg-nos-neutral-900 p-4 pb-0">
            <label
              htmlFor={form.ids.hasPin}
              aria-expanded={form.value.hasPin}
              aria-controls="pin-section"
              className="flex cursor-pointer items-center justify-between gap-4"
            >
              <span data-tid="text-has-pin" className="font-medium text-nos-neutral-0 font-text-p1">
                {t('TvProfiles.CreateProfile.Inputs.HasPin')}
              </span>
              <Toggle
                data-tid="toggle-has-pin"
                skin="woo-dark"
                id={form.ids.hasPin ?? 'hasPin'}
                checked={form.value.hasPin}
                onChange={() => {
                  form.setValue('hasPin', !form.value.hasPin);
                }}
              />
            </label>
            <section
              id="pin-section"
              aria-label={t('TvProfiles.CreateProfile.Inputs.HasPin')}
              aria-hidden={!form.value.hasPin}
              className="group mt-4 grid transition-[grid-template-rows] duration-200 data-[expanded=false]:grid-rows-[0fr] data-[expanded=true]:grid-rows-[1fr]"
              data-expanded={form.value.hasPin}
            >
              <div className="grid min-h-0 w-full gap-4 overflow-hidden">
                <div className="h-px w-full bg-nos-neutral-100" />
                <div className="grid gap-3 p-1 pb-4">
                  <Otp
                    data-tid="otp-pin"
                    length={widgets.PinCodeLength}
                    label={'TvProfiles.CreateProfile.Inputs.Pin'}
                    id="pin"
                    type="password"
                    skin="woo-dark"
                    errors={form.currentErrors.pin?.length ? [] : confirmPinErrors}
                    hideErrors={!!confirmPinErrors?.length}
                    hideSuccessMessages
                    value={form.value.pin ?? ''}
                    successMessages={
                      !confirmPinErrors?.length && !form.currentErrors.pin?.length
                        ? ['TvProfiles.CreateProfile.PinsIdentical']
                        : undefined
                    }
                    onChange={(v) => {
                      form.setValue('pin', v);
                    }}
                  />
                  <Otp
                    data-tid="otp-confirm-pin"
                    length={widgets.PinCodeLength}
                    label={'TvProfiles.CreateProfile.Inputs.ConfirmPin'}
                    id="confirmPin"
                    skin="woo-dark"
                    type="password"
                    errors={confirmPinErrors}
                    successMessages={
                      !confirmPinErrors?.length && !form.currentErrors.pin?.length
                        ? ['TvProfiles.CreateProfile.PinsIdentical']
                        : undefined
                    }
                    value={form.value.confirmPin ?? ''}
                    onChange={(v) => {
                      form.setValue('confirmPin', v);
                    }}
                  />
                </div>
              </div>
            </section>
          </div>
        </If>
      </div>
      <div className="sticky bottom-0 mt-auto w-full bg-nos-neutral-950 pt-8 max-tablet:grid max-tablet:pb-8">
        <div className="grid gap-4 data-[editing=true]:tablet:grid-cols-2" data-editing={isEditing}>
          <Button
            data-tid="button-submit-profile"
            skin="woo-dark"
            disabled={hasRequiredformError || form.loading || isDeletingProfile}
          >
            {form.loading ? (
              <GenericLoader className="size-6 animate-spin border-woo-green-100" />
            ) : (
              t('TvProfiles.CreateProfile.Submit')
            )}
          </Button>
        </div>
      </div>
    </form>
  );
}
